import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Loader2 } from 'lucide-react';

export function ProtectedRoute({ children, adminOnly = false, blockMRO = false, staffOnly = false, memberOnly = false, allowedRoles = null }) {
  const { user, loading, isAdmin, isStaff } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#000B18] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#00E5FF]" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (adminOnly && !isAdmin) {
    return <Navigate to="/dashboard" replace />;
  }

  if (staffOnly && !isStaff) {
    return <Navigate to="/dashboard" replace />;
  }

  // MROs are not members/subscribers; bounce them out of member-only screens.
  if (blockMRO && user.role === 'mro') {
    return <Navigate to="/mro" replace />;
  }

  // Strict member-only screens (wallet, referrals, certificate, membership card,
  // my-bookings, marketplace-as-buyer). Allowed for:
  //   - any subscriber (role==='member')
  //   - merchants who hold a dual account (role==='merchant' + active subscription)
  //   - admins (always — for ops/inspection)
  // Everyone else bounces to their role-appropriate home.
  const roleHomes = {
    admin: '/admin',
    moderator: '/admin/users',
    cro: '/admin/users',
    merchant: '/merchant',
    mro: '/mro',
    member: '/dashboard',
  };
  if (memberOnly && !isAdmin) {
    const subscriberOk = user.role === 'member' || (user.role === 'merchant' && user.subscription_status === 'active');
    if (!subscriberOk) {
      return <Navigate to={roleHomes[user.role] || '/dashboard'} replace />;
    }
  }

  // Generic per-route allowlist — admins always bypass for ops/inspection.
  // Dual-access flag: members approved as merchant partners can hit merchant
  // routes too. Treat them as effectively having both roles.
  const effectiveRoles = [user.role];
  if (user.is_merchant_partner) effectiveRoles.push('merchant');
  if (allowedRoles && !isAdmin && !allowedRoles.some(r => effectiveRoles.includes(r))) {
    return <Navigate to={roleHomes[user.role] || '/dashboard'} replace />;
  }

  return children;
}

export function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#000B18] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#00E5FF]" />
      </div>
    );
  }

  if (user) {
    let defaultRoute = '/dashboard';
    if (user.role === 'admin') defaultRoute = '/admin';
    else if (user.role === 'moderator' || user.role === 'cro') defaultRoute = '/admin/users';
    else if (user.role === 'merchant') defaultRoute = '/merchant';
    else if (user.role === 'mro') defaultRoute = '/mro';
    const from = location.state?.from?.pathname || defaultRoute;
    return <Navigate to={from} replace />;
  }

  return children;
}

/**
 * PublicOnlyRoute — forces the logged-in user out of the public marketing
 * site (root, pricing, CMS pages) and back into their dashboard. The user
 * asked for a strict "installed-app" feel: once you're in, you don't wander
 * the marketing site any more.
 */
export function PublicOnlyRoute({ children }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-[#000B18] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#00E5FF]" />
      </div>
    );
  }

  if (user) {
    let defaultRoute = '/dashboard';
    if (user.role === 'admin') defaultRoute = '/admin';
    else if (user.role === 'moderator' || user.role === 'cro') defaultRoute = '/admin/users';
    else if (user.role === 'merchant') defaultRoute = '/merchant';
    else if (user.role === 'mro') defaultRoute = '/mro';
    return <Navigate to={defaultRoute} replace />;
  }

  return children;
}
